import React, { useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import type { Stock } from '../../../output/models';

import { StockRow } from './StockRow';

import { color } from '../../../utils';

interface Props {
  stocks: Stock[];
}

export const StockTable: React.FC<Props> = ({ stocks }) => {
  const [stocksSorted, setStocksSorted] = useState<Stock[]>(stocks);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<string>('');

  const sortStock = (column: string) => {
    const currentDirection = column === sortColumn ? sortDirection : '';
    const nextDirection =
      currentDirection === 'down' ? 'up' : currentDirection === 'up' ? '' : 'down';

    if (nextDirection === 'up') {
      setStocksSorted(sortAscending(stocks, column));
    } else if (nextDirection === 'down') {
      setStocksSorted(sortDescending(stocks, column));
    } else {
      setStocksSorted(stocks);
    }

    setSortColumn(column);
    setSortDirection(nextDirection);
  };

  return (
    <table className="row-striped w-[600px] md:w-full">
      <thead>
        <tr className="bg-beige-light text-center">
          <th className="border border-gray p-2 md:w-[43px]"></th>
          <th className="cursor-pointer border border-gray p-1.5 p-2 md:w-[58px] md:p-2">
            <span className="whitespace-nowrap">コード</span>
          </th>
          <th className="border border-gray p-1.5 p-2 md:p-2">銘柄名</th>
          <th
            className="relative cursor-pointer border border-gray p-1.5 p-2 md:w-[65px] md:p-2"
            onClick={() => {
              sortStock('per');
            }}
          >
            PER
            {indicator('per', sortColumn, sortDirection)}
          </th>
          <th
            className="relative cursor-pointer border border-gray p-1.5 p-2 md:w-[65px] md:p-2"
            onClick={() => {
              sortStock('pbr');
            }}
          >
            PBR
            {indicator('pbr', sortColumn, sortDirection)}
          </th>
          <th
            className="relative cursor-pointer border border-gray p-1.5 p-2 md:w-[65px] md:p-2"
            onClick={() => {
              sortStock('operating_income_ratio');
            }}
          >
            OPR
            {indicator('operating_income_ratio', sortColumn, sortDirection)}
          </th>
          <th
            className="relative cursor-pointer border border-gray p-1.5 p-2 md:w-[65px] md:p-2"
            onClick={() => {
              sortStock('dividend_yield');
            }}
          >
            <span className="whitespace-nowrap">配当</span>
            {indicator('dividend_yield', sortColumn, sortDirection)}
          </th>
          <th
            className="relative cursor-pointer border border-gray p-1.5 p-2 md:w-[65px] md:p-2"
            onClick={() => {
              sortStock('change_rate');
            }}
          >
            前日比
            {indicator('change_rate', sortColumn, sortDirection)}
          </th>
          <th
            className="relative cursor-pointer border border-gray p-1.5 p-2 md:w-[65px] md:p-2"
            onClick={() => {
              sortStock('ytd');
            }}
          >
            年初来
            {indicator('ytd', sortColumn, sortDirection)}
          </th>
          <th className="border border-gray p-1.5 p-2 md:w-[75px] md:p-2">リンク</th>
        </tr>
      </thead>
      <tbody>
        {stocksSorted.map((stock, index) => (
          <StockRow stock={stock} key={index} />
        ))}
      </tbody>
    </table>
  );
};

const indicator = (
  column: string,
  sortColumn: string,
  sortDirection: string | undefined,
): JSX.Element | undefined => {
  if (column === sortColumn) {
    if (sortDirection === 'up') {
      return (
        <ArrowDropUpIcon
          className="absolute right-[-13px] md:top-[5px]"
          htmlColor={color('link', 'gray-header')}
        />
      );
    } else if (sortDirection === 'down') {
      return (
        <ArrowDropDownIcon
          className="absolute right-[-13px] md:top-[5px]"
          htmlColor={color('link', 'gray-header')}
        />
      );
    }
  }
};

const sortAscending = (stocks: Stock[], column: string): Stock[] => {
  return stocks.toSorted((a, b): number => {
    // @ts-ignore
    return (a[column] ?? 0) - (b[column] ?? 0);
  });
};

const sortDescending = (stocks: Stock[], column: string): Stock[] => {
  return stocks.toSorted((a, b): number => {
    // @ts-ignore
    return (b[column] ?? 0) - (a[column] ?? 0);
  });
};
